import React, { ReactElement } from 'react';

import COLORS from '~/constants/colors';
import Heart from '~/components/shapes/Heart';
import ProgressBar from '~/components/ProgressBar';

import { RendererProps } from '../../models';
import { INITIAL_HEALTH } from '../../constants/mechanics';
import { getPx } from '../../helpers';

function Metrics({ entity }: RendererProps): ReactElement {
  const pos = entity.getPos();
  const heartContainers = [...Array(INITIAL_HEALTH).keys()];
  const getKey = (idx) => `PagesFlowGoMetricsHearts${idx}`;

  // Metrics
  const maxHeartW = Math.floor(entity.dims.w / (INITIAL_HEALTH + 1));
  const maxHeartH = entity.dims.h / 2;
  const heartB = (entity.dims.b ?? 0) / 2.5;
  const heartM = entity.dims.m ?? 0;
  const heartW = Math.min(maxHeartW, maxHeartH) - heartM;
  const metricsW = 10 * (heartW + heartM);
  const metricsH = 2 * heartW + 3 * heartM;
  const metricsStyle = {
    position: 'absolute' as const,
    width: getPx(metricsW),
    height: getPx(metricsH),
    top: getPx(pos.y - metricsH - heartM),
    left: getPx(pos.x + heartM),
    zIndex: 5,
  };

  // Health
  const healthStyle = {
    display: 'flex',
    marginBottom: getPx(heartM),
    textAlign: 'left' as const,
    backgroundColor: COLORS.pickledBluewood,
    borderColor: COLORS.pickledBluewood15,
    borderRadius: getPx(2.5 * heartB),
    borderStyle: 'solid',
    borderWidth: `0 ${getPx(heartB)}`,
  };
  const heartStyle = {
    margin: getPx(heartM / 2),
  };
  const liveColor = (idx) =>
    entity.special && idx === 0 ? COLORS.black : COLORS.mandy;

  // Score
  const scoreHeight = getPx(heartW + heartM - 2);
  const scoreStyle = {
    width: getPx(metricsW),
    height: scoreHeight,
  };
  const containerStyle = {
    backgroundColor: COLORS.black50,
    borderColor: COLORS.white25,
    borderStyle: 'solid',
    borderWidth: getPx(1),
  };
  const loadedStyle = {
    backgroundColor: COLORS.dodgerBlue,
    borderStyle: 'none',
  };
  const pointsStyle = {
    color: COLORS.cornflower,
    fontSize: getPx(heartW / 1.5),
  };

  return (
    <div style={metricsStyle}>
      <div style={healthStyle}>
        {heartContainers.map((idx) => (
          <Heart
            key={getKey(idx)}
            width={getPx(heartW)}
            fill={entity.hearts > idx ? liveColor(idx) : COLORS.alto}
            style={heartStyle}
          />
        ))}
      </div>
      <div style={scoreStyle}>
        <ProgressBar
          points={entity.points}
          total={entity.total}
          width="100%"
          height={scoreHeight}
          containerStyle={containerStyle}
          loadedStyle={loadedStyle}
          pointsStyle={pointsStyle}
        />
      </div>
    </div>
  );
}

export default Metrics;
